import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Tag, Track } from '@app/core/models/Track';
import { RadioWatcherService } from '@app/core/ngxs/radio-watcher.service';
import { DataResolved } from '@app/core/resolvers/generic-resolver.resolver';
import { distinctUntilChangedWithSideEffectOnLike } from '@app/core/rxjs/rxOperators';
import { RadioState } from '@app/core/states/radio.state';
import { TagTracksHeaderComponent } from '@app/library/components/content/tag-tracks-header/tag-tracks-header.component';
import { ColumnShown } from '@app/library/models/column-shown.model';
import { SearchService } from '@app/library/services/search.service';
import { ColumnTypes, ToggleColumn } from '@app/library/states/columns.actions';
import { ColumnsState } from '@app/library/states/columns.state';
import {
  DeleteTagSuccess,
  RemoveTagFromTrackSuccess,
  RemoveTagsFromMultipleTrackSuccess,
  TagTracksRequest,
} from '@app/library/states/radio-tags.actions';
import { RadioTagsState } from '@app/library/states/radio-tags.state';
import { UnselectAll } from '@app/library/states/selected-tracks.actions';
import { TrackTagsSaveSuccess } from '@app/library/states/tracks.actions';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { isArray } from 'lodash';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';

@Component({
  selector: 'rk-tag-tracks-all',
  templateUrl: './tag-tracks-all.component.html',
  styleUrls: ['./tag-tracks-all.component.scss'],
})
export class TagTracksAllComponent implements OnInit, OnDestroy {
  @ViewChild('tagHeader', { static: false })
  header: TagTracksHeaderComponent;

  @Select(RadioTagsState.isLoadingTagTracks)
  isLoading$: Observable<boolean>;

  @Select(RadioTagsState.tagTracks)
  tracksUnfiltered$: Observable<Track[]>;

  @Select(ColumnsState.getColumnShow)
  columns$: Observable<ColumnShown>;

  currentTag$: Observable<Tag>;
  currentTag: Tag;
  tracks$: Observable<Track[]>;

  searchSubject = new BehaviorSubject<string>('');

  subscription = new Subscription();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly store: Store,
    private readonly watcher: RadioWatcherService,
    private readonly searchService: SearchService,
    private readonly actions$: Actions,
  ) {}

  ngOnInit() {
    this.currentTag$ = this.route.data.pipe(
      map<any, DataResolved<Tag>>(val => {
        this.currentTag = val.tag;

        return val.tag;
      }),
      tap(() => {
        if (this.header) {
          this.header.clearSearchBar();
          this.searchSubject.next('');
        }
      }),
      mergeMap(val => val.data$),
      filter(val => typeof val !== 'undefined'),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.subscription.add(
      this.actions$
        .pipe(
          ofActionDispatched(
            TrackTagsSaveSuccess,
            RemoveTagsFromMultipleTrackSuccess,
            RemoveTagFromTrackSuccess,
          ),
        )
        .subscribe(() => {
          const tagTracks = this.store.selectSnapshot(RadioTagsState.tagTracks);
          if (!tagTracks.length) {
            this.redirectToLibrary();
          }
        }),
    );

    this.subscription.add(
      this.actions$.pipe(ofActionDispatched(DeleteTagSuccess)).subscribe(() => {
        this.redirectToLibrary();
      }),
    );

    this.tracks$ = this.searchService
      .createTracksSearch(this.tracksUnfiltered$, this.searchSubject.asObservable())
      .pipe(
        filter(val => isArray(val)),
        distinctUntilChangedWithSideEffectOnLike(() =>
          this.store.dispatch(new UnselectAll()),
        ),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

    const tracksRequestSubscription = this.currentTag$
      .pipe(
        debounceTime(200),
        distinctUntilChanged((prev, curr) => prev.id === curr.id),
        switchMap(tag =>
          this.watcher.dispatchAndWatchAsObservable(new TagTracksRequest(tag)),
        ),
      )
      .subscribe(() => {});
    this.subscription.add(tracksRequestSubscription);
  }

  onColumnChange(column: ColumnTypes): void {
    this.store.dispatch(new ToggleColumn(column));
  }

  onChangeSearch(str: string) {
    if (typeof str === 'string') {
      this.searchSubject.next(str);
    }
  }

  @Dispatch()
  redirectToLibrary() {
    const radioId = this.store.selectSnapshot(RadioState.currentRadioId);

    return new Navigate(['radio', radioId, 'media', 'bac', 1], {}, { replaceUrl: true });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
